
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2, ChefHat } from 'lucide-react';

interface MealCompositionCardProps {
  composition: MealCompositionWithRecipes;
  onEdit?: (composition: MealCompositionWithRecipes) => void;
  onDelete?: (composition: MealCompositionWithRecipes) => void;
}

const MealCompositionCard: React.FC<MealCompositionCardProps> = ({
  composition,
  onEdit,
  onDelete
}) => {
  const recipeCount = composition.recipes.length;
  const firstThreeRecipes = composition.recipes.slice(0, 3);
  const remainingCount = Math.max(0, recipeCount - 3);

  return (
    <Card className="group hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="truncate">{composition.name}</span>
          <Badge variant="secondary" className="ml-2 flex-shrink-0">
            {recipeCount} recipe{recipeCount !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Recipe Preview */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Includes:</h4>
          <div className="space-y-1">
            {firstThreeRecipes.map((recipe) => (
              <div key={recipe.id} className="flex items-center gap-2 text-sm">
                <ChefHat size={12} className="text-muted-foreground flex-shrink-0" />
                <span className="truncate">{recipe.title}</span>
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ChefHat size={12} className="flex-shrink-0" />
                <span>+{remainingCount} more recipe{remainingCount !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Link to={`/composition/${composition.id}`} className="flex-1">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
            >
              View Details
            </Button>
          </Link>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit?.(composition)}
              >
                <Edit size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit Composition</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete?.(composition)}
              >
                <Trash2 size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Delete Composition</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
};

export default MealCompositionCard;
